import {
  Avatar,
  AvatarSize,
  AvatarStyle,
  ButtonHierarchy,
  ButtonSize,
  MainButton,
} from "@beydesign/storybook"
import {
  Box,
  Button,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material"
import React, { useState } from "react"
import { useNavigate } from "react-router-dom"
import { useAppContext, useAppDispatchContext } from "../app/Context"
import { getAccentColor } from "../constants/colors"
import { signUserOut } from "../external/auth"
import { StateAction } from "../types/State"
import { isAnonymous } from "../utils/func"
import { Logo } from "./logo"
import { ShareModal } from "./Modal/Share"

const emailAddress = "support@beyondpresence.ai"

export function Header() {
  const [shareModalOpen, setShareModalOpen] = useState<boolean>(false)

  const context = useAppContext()
  const { user, agent } = context || {}

  const isUserAnonymous = isAnonymous()
  const navigate = useNavigate()

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))

  const dispatch = useAppDispatchContext()

  const signOut = async () => {
    await signUserOut()
    dispatch({ type: StateAction.setUser, payload: null })
    if (agent?.id) {
      const path = agent.custom_branding ? `/agent/${agent.id}` : `/${agent.id}`
      navigate(path)
    }
  }

  const handleShare = async () => {
    setShareModalOpen(true)
  }

  const handleCreateMyAvatar = () => {
    try {
      const emailSubject = "Personal Avatar Request"
      const emailBody =
        "Hi, I am interested in a personal avatar, Please get in touch with me. Thanks!"
      const emailClient = `mailto:${emailAddress}?subject=${emailSubject}&body=${emailBody}`
      window.open(emailClient, "_blank")
    } catch (error) {
      console.error("Error sending request avatar email:", error)
    }
  }
  const handleContactUs = () => {
    try {
      const emailSubject = "Contact Us"
      const emailBody =
        "Hi, I am interested in contacting you. Please get in touch with me. Thanks!"
      const emailClient = `mailto:${emailAddress}?subject=${emailSubject}&body=${emailBody}`
      window.open(emailClient, "_blank")
    } catch (error) {
      console.error("Error sending contact us email:", error)
    }
  }

  const isUserValid = user != null && !isUserAnonymous

  return (
    <>
      {isMobile ? (
        <Stack
          direction="column"
          justifyContent="center"
          alignItems="center"
          spacing={2}
          sx={{ height: "5vh" }}
        >
          <Logo />
        </Stack>
      ) : (
        <Toolbar
          sx={{
            height: "10vh",
            justifyContent: "space-between",
            borderBottom: "1px solid #E7E6ED",
          }}
        >
          <Logo />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexGrow: 0,
              gap: "12px",
            }}
          >
            <MainButton
              hierarchy={ButtonHierarchy.Secondary}
              size={ButtonSize.lg}
              text="Share"
              leadingIcon="Share"
              showLeadingIcon
              onClick={handleShare}
            />
            {!agent?.custom_branding && (
              <>
                <MainButton
                  hierarchy={ButtonHierarchy.Primary}
                  size={ButtonSize.lg}
                  text="Create My Avatar"
                  onClick={handleCreateMyAvatar}
                />
                <MainButton
                  hierarchy={ButtonHierarchy.SecondaryColor}
                  size={ButtonSize.lg}
                  text="Contact Us"
                  onClick={handleContactUs}
                />
              </>
            )}
            {isUserValid && (
              <Stack direction="row" spacing={2}>
                <Stack alignItems="flex-end">
                  <Typography variant="body2" sx={{ mr: 1 }}>
                    {user?.email}
                  </Typography>
                  <Button
                    variant="text"
                    onClick={signOut}
                    sx={{ textTransform: "none", color: "black" }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color={getAccentColor()}
                    >
                      Switch account
                    </Typography>
                  </Button>
                </Stack>
                <Avatar
                  size={AvatarSize.lg}
                  image={user?.photoURL}
                  text={user?.name}
                  avatarStyle={
                    user.name
                      ? user?.photoURL
                        ? AvatarStyle.Picture
                        : AvatarStyle.Letters
                      : AvatarStyle.Placeholder
                  }
                />
              </Stack>
            )}
          </Box>
        </Toolbar>
      )}
      <ShareModal
        visible={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        agent={agent}
        agentImageUrl={undefined}
      />
    </>
  )
}
